import { AgGridReact } from 'ag-grid-react';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import VesselDcf from './VesselDcf';
import { IDcfVessel, TVesselListDcf } from '../dcfTypes';
import { formatValueAsString } from '../../../utils/helpers';
import InfoNote from '../../common/SharedComponents/Info-Note';
import { themeBalham } from 'ag-grid-enterprise';
import DynamicTab from '../../../shared/components/tabs/DynamicTab';
import { useMemo } from 'react';
import ImoAndNameCellRenderer from '../../../shared/components/renderers/customCellRenderers/ImoAndNameCellRenderer';
import OwnerCellRenderer from '../../../shared/components/renderers/customCellRenderers/OwnerCellRenderer';

export interface IFleetDcf {
	vesselListDcf: TVesselListDcf | null;
	selectedVessels: IDcfVessel[] | [];
}

const FleetDcf: React.FC<IFleetDcf> = ({ ...props }) => {
	interface IDcfSummaryItem {
		imoNumber: string;
		vesselName: string;
		sector: string;
		size: number | null;
		sizeUnit: string;
		built: string;
		owner: string;
		gearTankCargoContainment: string;
		fuelSystem: string;
		projectDcfValue: number;
	}

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			flex: 1,
			filter: 'agTextColumnFilter',
		};
	}, []);

	const colDefs: ColDef<IDcfSummaryItem>[] = [
		{
			headerName: '# Name',
			field: 'vesselName',
			headerTooltip: '# Name',
			cellStyle: (params) =>
				params.value === 'Total'
					? { backgroundColor: '#eeeeee', fontWeight: 'bold', color: 'black' }
					: { backgroundColor: '#eeeeee', fontWeight: 'normal', color: 'black' },
			aggFunc: () => 'Total',
			floatingFilter: true,
			cellRenderer: ImoAndNameCellRenderer,
			cellRendererParams: {
				// This is where you can pass additional parameters to the cell renderer if needed
				imoNumber: (params: any) => params.data.imoNumber,
			},
		},
		// { headerName: 'IMO Number', field: 'imoNumber', floatingFilter: true, headerTooltip: 'IMO Number' },
		{
			headerName: 'IMO Number',
			field: 'imoNumber',
			floatingFilter: true,
			headerTooltip: 'IMO Number',
			cellRenderer: ImoAndNameCellRenderer,
			cellRendererParams: {
				// This is where you can pass additional parameters to the cell renderer if needed
				imoNumber: (params: any) => params.data.imoNumber,
			},
		},
		{ headerName: 'Sector', field: 'sector', floatingFilter: true, filter: 'agSetColumnFilter', headerTooltip: 'Sector' },
		{
			headerName: 'Size',
			field: 'size',
			valueFormatter: (params: ValueFormatterParams<IDcfSummaryItem, any>) =>
				formatValueAsString(params.value, null, '', '', ' ' + params.data?.sizeUnit),
			floatingFilter: true,
			headerTooltip: 'Size',
		},
		{ headerName: 'Built', field: 'built', floatingFilter: true, headerTooltip: 'Built' },
		{ headerName: 'Owner', field: 'owner', floatingFilter: true, headerTooltip: 'Owner', cellRenderer: OwnerCellRenderer },
		{
			headerName: 'Gear / Tank',
			field: 'gearTankCargoContainment',
			floatingFilter: true,
			headerTooltip: 'Gear / Tank',
		},
		{ headerName: 'Fuel System', field: 'fuelSystem', floatingFilter: true, headerTooltip: 'Fuel System' },

		{
			headerName: 'Current DCF ($ Mn)',
			field: 'projectDcfValue',
			cellStyle: { backgroundColor: '#df6721', color: 'white' },
			headerTooltip: 'Current DCF ($ Mn)',
			valueFormatter: (params: any) => {
				return formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 1 }, '', '$', ' Mn');
			},
			aggFunc: () => {
				const totalDcfValue = data.reduce((sum, vessel) => sum + vessel.projectDcfValue, 0);
				return totalDcfValue;
			},
			floatingFilter: true,
		},
	];

	const data: IDcfSummaryItem[] = props.selectedVessels.map((vessel) => ({
		vesselName: vessel.name,
		imoNumber: vessel.imoNumber.toString(),
		sector: vessel.sectorName,
		size: vessel.size,
		sizeUnit: vessel.sizeUnit,
		built: vessel.yearOfDelivery.toString(),
		owner: vessel.owner,
		gearTankCargoContainment: vessel.gearTankCargoContainment,
		fuelSystem: vessel.fuelSystem,
		projectDcfValue: Number(
			props.vesselListDcf?.dataList?.find((v) => v.imoNumber === vessel.imoNumber)?.financialMetrics?.projectDcfValue ?? 0,
		),
	}));

	const rowData = data;

	return (
		<DynamicTab
			dynamicItem={VesselDcf}
			dynamicItemProps={(item: any) => ({
				vesselDcf:
					props.vesselListDcf?.dataList?.length || 0 > 0
						? props.vesselListDcf?.dataList?.find((v) => v.imoNumber === item.imoNumber) || null
						: null,
				hideTab: true,
				data: item,
			})}
			dynamicItemData={props.selectedVessels}
			staticItems={[
				<div className="flex flex-col w-full h-full">
					<div className="w-full h-full">
						<AgGridReact
							columnDefs={colDefs}
							rowData={rowData}
							theme={themeBalham}
							defaultColDef={defaultColDef}
							grandTotalRow="bottom"
						/>
					</div>
					<div className="mt-4">
						<InfoNote />
					</div>
				</div>,
			]}
			tabColor="msi-chart-orange"
			giveSpace
		/>
	);
};

export default FleetDcf;
